type StorageName = 'localStorage' | 'sessionStorage';

const getStorage = (storageName: StorageName): Storage => {
  if (typeof window === 'undefined') {
    throw new Error(
      `${storageName} can only be accessed in a browser environment`
    );
  }
  return window[storageName];
};

const safeStorageSetItem = (
  storageName: StorageName,
  key: string,
  value: string
): Error | undefined => {
  try {
    getStorage(storageName).setItem(key, value);
    return;
  } catch (e) {
    const error =
      e instanceof Error ? e : new Error('Error attempting to set item');
    return error;
  }
};

const safeStorageGetItem = (
  storageName: StorageName,
  key: string
): [string | null, null] | [null, Error] => {
  try {
    const result = getStorage(storageName).getItem(key);
    return [result, null];
  } catch (e) {
    const error =
      e instanceof Error ? e : new Error('Error attempting to get item');
    return [null, error];
  }
};

const safeStorageRemoveItem = (
  storageName: StorageName,
  key: string
): Error | undefined => {
  try {
    getStorage(storageName).removeItem(key);
    return;
  } catch (e) {
    const error =
      e instanceof Error ? e : new Error('Error attempting to remove item');
    return error;
  }
};

export const safeSessionStorageGetItem = (key: string) =>
  safeStorageGetItem('sessionStorage', key);

export const safeSessionStorageSetItem = (key: string, value: string) =>
  safeStorageSetItem('sessionStorage', key, value);

export const safeSessionStorageRemoveItem = (key: string) =>
  safeStorageRemoveItem('sessionStorage', key);

export const safeLocalStorageGetItem = (key: string) =>
  safeStorageGetItem('localStorage', key);

export const safeLocalStorageSetItem = (key: string, value: string) =>
  safeStorageSetItem('localStorage', key, value);

export const safeLocalStorageRemoveItem = (key: string) =>
  safeStorageRemoveItem('localStorage', key);
